import { useStrings } from "@1js/localization-components";
import { useMsal } from "@azure/msal-react";
import {
  Dialog,
  DialogSurface,
  Toaster,
  makeStyles,
  useId,
  type DialogOpenChangeData,
  type DialogOpenChangeEvent,
} from "@fluentui/react-components";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { ButtonRefContext } from "../../contexts/ButtonRefContext.web";
import { ManageSharedPromptsButtonRefId } from "../../contexts/utils/constants";
import { useToast } from "../../hooks/toast/useToast";
import { PropertyContext } from "../../pages/PropertyContext.web";
import { MetaOSAppContext } from "../../pages/metaos/MetaOSAppContext";
import {
  fetchLinkedPrompts,
  removeLinkedPromptAsync,
  type LinkedPrompt,
} from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import { isMetaOsHelper } from "../../utils/url";
import { ConfirmationDialog } from "./ConfirmationDialog";
import {
  FailureStopShareToastMessage,
  ManageAccessDialogTitle,
  SuccessStopShareToastMessage,
} from "./ManageSharedPrompts.strings";
import { SharedPromptsDialog } from "./SharedPromptsDialog.web";
import useManageAccessDialog from "./useManageAccessDialog";

const useStyles = makeStyles({
  dialog: {
    "@media (max-width: 540px)": {
      display: "flex",
      maxWidth: "100vw",
      maxHeight: "90%",
      marginBottom: "0px",
    },
  },
});

export const SharedPromptsDialogContainer: React.FC = () => {
  const strings = useStrings({
    SuccessStopShareToastMessage,
    FailureStopShareToastMessage,
    ManageAccessDialogTitle,
  });

  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const styles = useStyles();
  const telemetry = useTelemetry();
  const toasterId = useId("toaster");
  const [vertical, setVertical] = React.useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const { notify: _notify } = useToast(toasterId, 3000);
  const { state, dispatch } = useManageAccessDialog();
  const [siteSetting] = React.useContext(PropertyContext);

  const { focusButton } = React.useContext(ButtonRefContext);

  React.useEffect(() => {
    const notify = (...props: Parameters<typeof _notify>) => {
      const dialog = document.getElementById("dialog-title-id");
      setVertical(dialog?.getBoundingClientRect().top || 108); // 108 is the default value for margin between the top of the page and the dialog.
      _notify(...props);
    };

    if (state.toast === "success") {
      notify("success", strings.SuccessStopShareToastMessage());
      telemetry.sendNotificationActionEvent(
        "UnsharePost",
        200,
        "StopSharingButton"
      );
    } else if (state.toast === "error") {
      notify("error", strings.FailureStopShareToastMessage());
      telemetry.sendNotificationActionEvent(
        "UnsharePost",
        500,
        "StopSharingButton"
      );
    }
  }, [state.toast, telemetry, strings, _notify]);

  React.useEffect(() => {
    const manageAccessOpenFunction = async () => {
      dispatch({ type: "OPEN_DIALOG" });
      const serviceHost = window.location.hostname.toString();

      const fetchedPrompts: LinkedPrompt[] = siteSetting.user
        ? await fetchLinkedPrompts(
            serviceHost,
            telemetry.sendApiActionEvent,
            instance
          )
        : [];
      dispatch({ type: "LAUNCH_LIST", payload: fetchedPrompts });
      telemetry.sendSettingsActionEvent("Open", "SharedLinksSetting");
    };
    const getPrompts = async () => {
      if (isMetaOsHelper()) {
        if (metaOSAppContext.manageAccessDialog) {
          metaOSAppContext.manageAccessDialog = false;
          metaOSAppContext.updateContext(metaOSAppContext);
          manageAccessOpenFunction();
        }
      } else {
        if (searchParams.get("manageAccess") === "open") {
          manageAccessOpenFunction();
        }
      }
    };
    getPrompts();
  }, [
    dispatch,
    instance,
    metaOSAppContext,
    searchParams,
    siteSetting.user,
    telemetry,
  ]);

  const handleDeletePrompt = React.useCallback(
    (prompt: LinkedPrompt) => {
      telemetry.sendSettingsActionEvent("StopSharing", "SharedLinksSetting");
      dispatch({ type: "DELETE_PROMPT", payload: prompt });
    },
    [dispatch, telemetry]
  );

  const handleDeletePromptInit = React.useCallback(async () => {
    telemetry.sendSettingsActionEvent("Save", "SharedLinksSetting");
    dispatch({ type: "DELETE_PROMPT_INIT" });
    try {
      if (!state.activePrompt) {
        throw new Error("Prompt ID is missing");
      }
      const serviceHost = window.location.hostname.toString();

      await removeLinkedPromptAsync(
        serviceHost,
        [state.activePrompt.PromptId],
        telemetry.sendApiActionEvent,
        instance
      );
      telemetry.sendUnsharePromptEvent(state.activePrompt);

      //once the API call is successful, show toast and call DELETE_PROMPT_SUCCEED
      dispatch({ type: "DELETE_PROMPT_SUCCEED" });
    } catch (err) {
      //if API call fails, show toast and call DELETE_PROMPT_FAILED
      dispatch({ type: "DELETE_PROMPT_FAILED" });
    }
  }, [dispatch, instance, state.activePrompt, telemetry]);

  const cancelDelete = React.useCallback(() => {
    telemetry.sendSettingsActionEvent("Cancel", "SharedLinksSetting");
    dispatch({ type: "DELETE_PROMPT_CANCEL" });
  }, [dispatch, telemetry]);

  const handleOnDialogOpenChanged = (
    _event: DialogOpenChangeEvent,
    data: DialogOpenChangeData
  ) => {
    if (!data.open) {
      searchParams.delete("manageAccess");
      setSearchParams(searchParams);
      focusButton(ManageSharedPromptsButtonRefId);
      dispatch({ type: "CLOSE_LIST" });
    }
  };

  return (
    <>
      <Toaster
        toasterId={toasterId}
        offset={{ vertical: vertical }}
        pauseOnHover
        pauseOnWindowBlur
        aria-live="assertive"
        role="alert"
        aria-atomic="true"
      />
      <Dialog
        open={state.open}
        onOpenChange={handleOnDialogOpenChanged}
        data-testid="manage-shared-prompts-dialog"
      >
        <DialogSurface className={styles.dialog}>
          {state.surface === "list" ? (
            <SharedPromptsDialog
              onDeletePrompt={handleDeletePrompt}
              prompts={state.linkedPrompts}
              isLoading={state.status === "loading"}
            />
          ) : (
            <ConfirmationDialog
              deletePrompt={handleDeletePromptInit}
              cancelDelete={cancelDelete}
              isLoading={state.status === "loading"}
            />
          )}
        </DialogSurface>
      </Dialog>
    </>
  );
};
